import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { FilterDescriptor } from '@progress/kendo-react-dropdowns/dist/npm/common/filterDescriptor';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import CircularIconButton from 'src/components/design-system/CircularIconButton';
import MultiSelectBox from 'src/components/design-system/MultiSelectBox';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { GLCodePeriodBudgetResponse } from 'src/services/useGlCodePeriodBudgetApi';
import { preventDefault } from 'src/utils';
import { errorIcon, getBgColor } from 'src/utils/icons';

interface MultiSelectDepartmentProps {}

const GLCodeAttributeDropDown: FC<MultiSelectDepartmentProps> = () => {
  const { isEditMode, setEditingGlCodePeriodBudget, setGlCodePeriodBudget, glCodePeriodBudget } =
    useViewCheckbook();
  const {
    t,
    isBulkManageGLCode,
    selectedGLCode,
    setSelectedGLCode,
    isSelectedGLCodeEmpty,
    isManageGLCodeAttributesDirty,
    confirmBeforeProceedingToMainScreen,
    handleGLCodeAttributes,
    refreshBulkAssign,
  } = usePeriodView();

  const activeGLCodes = useMemo(
    () =>
      glCodePeriodBudget
        ?.sort((a, b) => a?.sortKey.localeCompare(b?.sortKey))
        ?.filter((period) => period.isActive),
    [glCodePeriodBudget]
  );

  const [data, setData] = useState(activeGLCodes);

  useEffect(() => {
    if (activeGLCodes) setData(activeGLCodes);
  }, [activeGLCodes]);

  const filterData = useCallback(
    (filter: FilterDescriptor | CompositeFilterDescriptor) => {
      const data = [...activeGLCodes].slice();
      return filterBy(data, filter);
    },
    [activeGLCodes]
  );
  const filterChange = useCallback(
    (event: DropDownListFilterChangeEvent) => {
      setData(filterData(event.filter));
    },
    [filterData]
  );

  const onCheckbookSelection = useCallback(
    (rowIds: string[]) => {
      (isEditMode ? setEditingGlCodePeriodBudget : setGlCodePeriodBudget)(
        (items: GLCodePeriodBudgetResponse[]) =>
          [...items].map((item) => ({ ...item, selected: rowIds.includes(item?.rowId) }))
      );
    },
    [isEditMode, setEditingGlCodePeriodBudget, setGlCodePeriodBudget]
  );

  const handleBulkManageGLCodeChange = useCallback(
    (values: GLCodePeriodBudgetResponse[]) => {
      const rowIds = values.map((v) => v.rowId);
      onCheckbookSelection(rowIds);
      setSelectedGLCode(values);
    },
    [onCheckbookSelection, setSelectedGLCode]
  );
  const dropDownIndex = useMemo(
    () =>
      activeGLCodes
        ? activeGLCodes?.findIndex((glCode) => glCode.id === selectedGLCode[0]?.id)
        : -1,
    [activeGLCodes, selectedGLCode]
  );

  const handleManageGLCodeChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e.target;

      if (isManageGLCodeAttributesDirty) {
        confirmBeforeProceedingToMainScreen(() => {
          handleGLCodeAttributes(value);
        });
      } else {
        handleGLCodeAttributes(value);
      }
      refreshBulkAssign();
    },
    [
      confirmBeforeProceedingToMainScreen,
      handleGLCodeAttributes,
      isManageGLCodeAttributesDirty,
      refreshBulkAssign,
    ]
  );

  const handleGLCodeNavigation = useCallback(
    (indexAdjustment: number) => {
      const newIndex = dropDownIndex + indexAdjustment;
      const newGLCode = activeGLCodes[newIndex];

      if (isManageGLCodeAttributesDirty) {
        confirmBeforeProceedingToMainScreen(() => {
          handleGLCodeAttributes(newGLCode);
        });
      } else {
        handleGLCodeAttributes(newGLCode);
      }
      refreshBulkAssign();
    },
    [
      dropDownIndex,
      activeGLCodes,
      isManageGLCodeAttributesDirty,
      refreshBulkAssign,
      confirmBeforeProceedingToMainScreen,
      handleGLCodeAttributes,
    ]
  );

  const isLeftBtnDropDwnDisabled: boolean = useMemo(() => {
    if (dropDownIndex - 1 < 0) return true;
    else return false;
  }, [dropDownIndex]);

  const isRightBtnDropDwnDisabled: boolean = useMemo(() => {
    if (dropDownIndex + 1 >= activeGLCodes.length) return true;
    else return false;
  }, [dropDownIndex, activeGLCodes.length]);

  const onLeftBtnDropDwnClick = useCallback(() => {
    handleGLCodeNavigation(-1);
  }, [handleGLCodeNavigation]);

  const onRightBtnDropDwnClick = useCallback(() => {
    handleGLCodeNavigation(1);
  }, [handleGLCodeNavigation]);

  const errorMessage = useMemo(
    () => (isSelectedGLCodeEmpty ? ['Please select GL Code(s)'] : []),
    [isSelectedGLCodeEmpty]
  );

  return (
    <div onDragStart={preventDefault} onDragOver={preventDefault}>
      {isBulkManageGLCode ? (
        <PopUp
          popupAlign={{ horizontal: 'right', vertical: 'top' }}
          anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
          content={<PopUpContentWrapper type="Error" contents={errorMessage} />}
          show={isSelectedGLCodeEmpty}
        >
          <div className={`${getBgColor(errorMessage)} p-1 flex gap-1 items-center`}>
            <p className="text-sm">{t('Click to Search and Select a GL Code')}</p>
            {isSelectedGLCodeEmpty && <span>{errorIcon}</span>}
            <div className="flex-1">
              <MultiSelectBox
                data={activeGLCodes}
                value={selectedGLCode}
                setValue={handleBulkManageGLCodeChange}
                dataItemKey="id"
                textField="label"
                valid={!isSelectedGLCodeEmpty}
              />
            </div>
          </div>
        </PopUp>
      ) : (
        <div className="search-dropdown flex items-center gap-2">
          <p className="py-2">
            <label className="w-full text-sm" htmlFor="search-input">
              {t('Select a GL Code')}
            </label>
          </p>
          <div className="flex items-center gap-2 mt-1 w-[75%] px-4">
            <span
              id="arrow-end-left-anchor"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={
                isLeftBtnDropDwnDisabled ? 'No Previous GL Code' : 'Previous GL Code'
              }
            >
              <CircularIconButton
                icon="arrow-end-left"
                disabled={isLeftBtnDropDwnDisabled}
                onClick={onLeftBtnDropDwnClick}
              />
            </span>
            <DropDownList
              data={data}
              onChange={handleManageGLCodeChange}
              value={selectedGLCode[0]}
              textField="label"
              dataItemKey="id"
              filterable
              onFilterChange={filterChange}
              style={{ width: '50%' }}
            />
            <span
              id="arrow-end-right-anchor"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={isRightBtnDropDwnDisabled ? 'No Next GL Code' : 'Next GL Code'}
            >
              <CircularIconButton
                icon="arrow-end-right"
                disabled={isRightBtnDropDwnDisabled}
                onClick={onRightBtnDropDwnClick}
              />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GLCodeAttributeDropDown;
